import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

import { SuggestionStatus } from "@/components/SuggestionStatus";
import { listSuggestions } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useSkillPath } from "@/lib/skillPath";
import type { Suggestion } from "@/lib/types";
import { useDelayedFlag } from "@/lib/useDelayedFlag";

// Terminal statuses — a decision has been made and nothing more will happen. Everything else
// (pending / processing / ready / conflict) is still actionable and belongs under "Open".
const CLOSED_STATUSES = new Set(["merged", "rejected"]);

function toUtc(iso: string) {
  // Append 'Z' when the server omits a timezone indicator so browsers
  // don't misparse the timestamp as local time instead of UTC.
  return new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z");
}

function relTime(iso: string) {
  const m = Math.floor((Date.now() - toUtc(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Suggestion queue for a skill, split into two tabs:
 * - **Open** — still in flight or awaiting a decision (pending, processing, ready, conflict).
 * - **Closed** — a final decision was reached (merged or rejected).
 *
 * While anything is processing the list polls every 3s so status pills update live.
 */
export default function SuggestionQueue({ slug, canReview = true }: { slug: string; canReview?: boolean }) {
  const skillPath = useSkillPath();
  const { user, accessToken } = useAuth();
  const [items, setItems] = useState<Suggestion[] | null>(null);
  const [tab, setTab] = useState<"open" | "approved">("open");

  const load = useCallback(async () => {
    if (!accessToken) return;
    try {
      setItems(await listSuggestions(slug, accessToken));
    } catch {
      setItems([]);
    }
  }, [slug, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!items || !items.some((s) => s.status === "processing")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [items, load]);

  // Defer the loading placeholder so a fast load goes straight to content without a flash.
  const showLoading = useDelayedFlag(!items);

  if (!accessToken) return null;
  if (!items) return showLoading ? <p className="text-sm text-text/40">Loading…</p> : null;

  const open = items.filter((s) => !CLOSED_STATUSES.has(s.status));
  const approved = items.filter((s) => s.status === "merged");
  // Owners get the Open/Approved split; non-owners only ever receive approved (merged) suggestions
  // from the API, so they get a single flat "approved" list with no tabs.
  const shown = canReview ? (tab === "open" ? open : approved) : items;

  const tabBtn = (id: "open" | "approved", label: string, n: number) => (
    <button
      onClick={() => setTab(id)}
      className={`-mb-px flex items-center gap-2 border-b-2 pb-2.5 font-medium transition ${
        tab === id ? "border-primary text-text" : "border-transparent text-text/50 hover:text-text/80"
      }`}
    >
      {label}
      <span
        className={`rounded-full px-1.5 py-0.5 text-[11px] ${
          tab === id ? "bg-primary/15 text-primary" : "bg-black/[0.05] text-text/45 dark:bg-white/10"
        }`}
      >
        {n}
      </span>
    </button>
  );

  return (
    <div>
      {canReview && (
        <div className="mb-4 flex gap-6 border-b border-[var(--border)] text-sm">
          {tabBtn("open", "Open", open.length)}
          {tabBtn("approved", "Approved", approved.length)}
        </div>
      )}

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--border)] px-6 py-10 text-center text-sm text-text/45">
          {!canReview
            ? "No approved changes yet."
            : tab === "open"
              ? "No open suggestions right now."
              : "Approved changes will show up here."}
        </div>
      ) : (
        <ul className="space-y-2.5">
          {shown.map((s) => {
            const isAgent = s.source === "agent";
            const detailUrl = skillPath(slug, `/suggestions/${s.id}`);
            return (
              <li
                key={s.id}
                className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] transition hover:border-primary/40"
                style={{ boxShadow: "var(--shadow-card)" }}
              >
                {/* Clickable header area → navigates to detail */}
                <Link to={detailUrl} className="flex items-start gap-3 p-4 pb-2">
                  {/* Source icon */}
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isAgent ? "bg-cyan-500/10" : "bg-primary/10"}`}>
                    {isAgent ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-400">
                        <rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4M8 15h.01M16 15h.01" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary/70">
                        <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                      </svg>
                    )}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex items-start justify-between gap-2">
                      <span className="truncate font-medium">{s.title || s.message}</span>
                      <span className="shrink-0 text-xs text-text/30">{relTime(s.created_at)}</span>
                    </span>
                    <span className="mt-1 block text-xs text-text/40">
                      {isAgent ? (
                        <span className="font-medium text-cyan-400/80">{s.agent_name ?? "agent"}</span>
                      ) : (
                        <span className="font-medium text-primary/60">
                          {user && s.author_id === user.id ? "You" : (s.author_name || "Contributor")}
                        </span>
                      )}
                    </span>
                  </span>
                </Link>

                {/* Status — separate from the Link to allow expand without navigating */}
                <div className="px-4 pb-4 pt-1 pl-[3.75rem]">
                  <SuggestionStatus s={s} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
